import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { getClient } from '@knowsuchagency/django-allauth';
import { saveTokens } from '@/auth';
import { toast } from 'sonner';

export const Route = createFileRoute('/auth/verify-email/$key')({
  component: VerifyEmail,
});

function VerifyEmail() {
  const navigate = useNavigate();
  const { key } = Route.useParams();
  const client = getClient();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function handleEmailVerification() {
      try {
        // First ensure we have a CSRF token by calling getConfiguration
        // This is necessary in incognito/new sessions where no cookie exists
        await client.getConfiguration();

        // Call the verifyEmail API with the key from the URL
        const response = await client.verifyEmail({ key });

        // Check if we received an authenticated response with tokens
        if (response.status === 200 && 'user' in response.data) {
          // Extract tokens from the response
          const userData: any = response.data.user;

          if (userData?.access && userData?.refresh) {
            // Save tokens to localStorage
            saveTokens({
              access: userData.access,
              refresh: userData.refresh,
            });
            // Show success message
            toast.success('Email Verified', {
              description: 'Your email has been successfully verified.',
            });
            // Navigate to the organization page on success
            navigate({ to: '/organization' });
          } else {
            // Email verified but no tokens - might need additional authentication
            setError('Email verified successfully, but authentication tokens were not provided. Please log in.');
          }
        } else if (response.status === 401) {
          // Email verification successful but additional steps needed
          setError('Email verification requires additional authentication. Please log in.');
        }
      } catch (err) {
        console.error('Email verification error:', err);
        setError('Email verification failed. The link may be invalid or expired. Please request a new verification email.');
      } finally {
        setIsLoading(false);
      }
    }

    if (key) {
      handleEmailVerification();
    } else {
      setError('No verification key provided.');
      setIsLoading(false);
    }
  }, [key, client, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Verification Error
          </h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate({ to: '/login' })}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <Spinner className="h-12 w-12 mx-auto mb-4" />
        <p className="text-gray-600">Verifying your email...</p>
      </div>
    </div>
  );
}
